import type { TypedWrappedFieldProps } from "redux-form";
import classNames from "classnames";
import _ from "underscore";

import { Typography } from "@braintrust/braintrust-ui-components";
import type { TextFieldProps } from "@js/forms/fields/text";
import { TextField } from "@js/forms/fields/text";

import styles from "./styles.module.scss";

/**
 * Example usage:
 *
 * ```
 * <Field
 *     name="message"
 *     minLength={4}
 *     maxLength={10}
 *     label="Your Message"
 *     component={TextareaField} />
 * ```
 */

type TextareaFieldProps = {
  minLength?: number;
  maxLength?: number;
  maxLabel?: boolean;
  markdown?: boolean;
  maxLengthInHelpText?: boolean;
  maxLengthCounterThreshold?: number;
} & TextFieldProps &
  TypedWrappedFieldProps<string>;

export const TextareaField = (props: TextareaFieldProps) => {
  const {
    minLength,
    maxLabel,
    maxLength,
    input: { value },
    fullWidth,
    className,
    markdown,
    helperText,
    maxLengthInHelpText,
    maxLengthCounterThreshold,
  } = props;

  const textLength = value.length;

  const showMaxLengthCounter = maxLengthCounterThreshold
    ? textLength >= maxLengthCounterThreshold
    : maxLength;

  const showAdditionalText = !!minLength || !!maxLabel || !!markdown;

  return (
    <div
      className={classNames("textarea", styles.textarea, className)}
      style={fullWidth ? { width: "100%" } : undefined}
    >
      {!maxLengthInHelpText && maxLength && showMaxLengthCounter && (
        <Typography
          component="p"
          className={classNames(styles.textareaMaxLength, {
            [styles.textareaMaxLengthError]: textLength > maxLength,
          })}
        >
          {textLength} / {maxLength}
        </Typography>
      )}
      <TextField
        multiline
        rows={!props.minRows && !props.maxRows && !props.rows ? 4 : props.rows}
        helperText={
          maxLengthInHelpText ? (
            <span
              className={classNames(
                "textarea__helper-text-wrapper",
                styles.textareaHelperTextWrapper,
              )}
            >
              {helperText}

              {maxLength && (
                <Typography
                  component="span"
                  className={classNames(
                    styles.textareaMaxLength,
                    styles.textareaMaxLengthStatic,
                    {
                      [styles.textareaMaxLengthError]: textLength > maxLength,
                    },
                  )}
                >
                  {textLength} / {maxLength}
                </Typography>
              )}
            </span>
          ) : (
            helperText
          )
        }
        {..._.omit(
          props,
          "rows",
          "minLength",
          "maxLength",
          "maxLabel",
          "className",
          "markdown",
          "helperText",
          "maxLengthInHelpText",
          "maxLengthCounterThreshold",
        )}
      />
      {showAdditionalText && (
        <Typography component="p" size="small" sx={{ pt: 1 }}>
          {minLength && <span>Min {minLength} characters. </span>}
          {maxLabel && <span>Max {maxLength} characters. </span>}
          {markdown && (
            <span>
              You can use
              <Typography
                component="a"
                fontWeight={500}
                className={styles.textareaLink}
                href="https://help.github.com/en/github/writing-on-github/basic-writing-and-formatting-syntax"
                target="_blank"
                rel="noopener noreferrer"
              >
                basic markdown syntax
              </Typography>
              .
            </span>
          )}
        </Typography>
      )}
    </div>
  );
};
